/**
 * Cabin Selector Dialog
 * =====================
 * Displays list of available hardware cabin microphones and allows user to select one
 * before starting a consultation session in hardware mode.
 *
 * Part of: Jetson Hybrid Audio Integration
 * Created: 2026-01-05
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Cabin,
  CheckCircle,
  Cancel,
  LocationOn,
  Mic
} from '@mui/icons-material';
import { sessionAPI } from '../../services/api';

export interface Cabin {
  cabin_id: string;
  name: string;
  location: string;
  microphone: string;
  camera: string;
  camera_enabled: boolean;
  is_available: boolean;
  active_session?: {
    session_id: string;
    clinician_id: number;
    patient_id: number;
    started_at: string;
  };
}

interface CabinSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (cabinId: string) => void;
}

export default function CabinSelectorDialog({
  open,
  onClose,
  onSelect
}: CabinSelectorDialogProps) {
  const [cabins, setCabins] = useState<Cabin[]>([]);
  const [selectedCabin, setSelectedCabin] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch cabins when dialog opens
  useEffect(() => {
    if (open) {
      fetchCabins();
    }
  }, [open]);

  const fetchCabins = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all cabins from backend
      const response = await sessionAPI.getCabins();
      const cabinsData = response.data.cabins || [];

      setCabins(cabinsData);

      // Auto-select first available cabin
      const firstAvailable = cabinsData.find((c: Cabin) => c.is_available);
      if (firstAvailable) {
        setSelectedCabin(firstAvailable.cabin_id);
      }
    } catch (err: any) {
      console.error('Failed to fetch cabins:', err);
      setError(err.response?.data?.detail || 'Failed to load cabins');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedCabin) {
      setError('Please select a cabin');
      return;
    }

    onSelect(selectedCabin);
  };

  const availableCabins = cabins.filter(c => c.is_available);
  const unavailableCabins = cabins.filter(c => !c.is_available);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cabin color="primary" />
          <Typography variant="h6">Select Consultation Cabin</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && cabins.length === 0 && (
          <Alert severity="warning">
            No hardware cabins configured. Please contact your administrator.
          </Alert>
        )}

        {!loading && availableCabins.length === 0 && unavailableCabins.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            All cabins are currently in use. Please wait or use browser mode.
          </Alert>
        )}

        {!loading && cabins.length > 0 && (
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedCabin}
              onChange={(e) => setSelectedCabin(e.target.value)}
            >
              {/* Available Cabins */}
              {availableCabins.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="success.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Available Cabins ({availableCabins.length})
                  </Typography>

                  {availableCabins.map((cabin) => (
                    <Paper
                      key={cabin.cabin_id}
                      elevation={selectedCabin === cabin.cabin_id ? 3 : 1}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: selectedCabin === cabin.cabin_id ? 2 : 1,
                        borderColor: selectedCabin === cabin.cabin_id ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          elevation: 3,
                          borderColor: 'primary.main'
                        }
                      }}
                      onClick={() => setSelectedCabin(cabin.cabin_id)}
                    >
                      <FormControlLabel
                        value={cabin.cabin_id}
                        control={<Radio />}
                        label={
                          <Box sx={{ width: '100%' }}>
                            {/* Cabin Name */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {cabin.name}
                              </Typography>
                              <Chip
                                icon={<CheckCircle />}
                                label="Available"
                                color="success"
                                size="small"
                                sx={{ height: 20 }}
                              />
                            </Box>

                            {/* Location */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {cabin.location}
                              </Typography>
                            </Box>

                            {/* Microphone */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Mic fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {cabin.microphone}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  ))}
                </>
              )}

              {/* Unavailable Cabins */}
              {unavailableCabins.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="error" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                    In Use ({unavailableCabins.length})
                  </Typography>

                  {unavailableCabins.map((cabin) => (
                    <Paper
                      key={cabin.cabin_id}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'action.disabledBackground',
                        opacity: 0.6
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Cancel color="error" sx={{ mt: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {cabin.name}
                            </Typography>
                            <Chip
                              label="In Use"
                              color="error"
                              size="small"
                              sx={{ height: 20 }}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {cabin.location}
                            </Typography>
                          </Box>

                          {cabin.active_session && (
                            <Typography variant="caption" color="text.secondary">
                              Session started: {new Date(cabin.active_session.started_at).toLocaleTimeString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </>
              )}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Back
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!selectedCabin || loading}
        >
          Start Visit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
